import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SHIPPING_TYPES } from './constants';
import { findMultiPackageShippingMethod, findShippingMethod } from './utils';
import PackageQuantitySelect from './PackageQuantitySelect';
import PackageSelect from './PackageSelect';
import ShippingMethodSelect from "./ShippingMethodSelect";
import { PACKAGE_OPTIONS } from "../RecommendedPackageSection/constants";
import { getShippingGroupCostsForPackage } from "../../../../../services/orderServices";

const STORE_IDS_FOR_COMBOBOX_AUTOFOCUS = []

const ShippingPackageDataSection = ({
    onPackageNumberChange = (newValue) => { },
    onShippingMethodChange = (newValue) => { },
    onShippingPackageChange = (newValue) => { },
    initialPackageQuantity = 1,
    initialShippingMethod,
    initialShippingPackages = null,
    availableShippingMethods,
    availableShippingPackages,
    storePackingOptions,
    orderWarehouseId,
    orderStoreId = null,
    isShippingMethodEditable = false,
    isPackageQuantityEditable = true,
    shippingMethodOptions,
    isPackagelessOrder,
    hasPackagelessProduct,
    isFragileOrder,
    disableMultiPackageAutoSelect = false,
    orderCanRecalculateShippingMethod = true,
    orderId = null,
    onBusyChange = (isBusy) => { },
    firstPackageInputRef = null,
    focusPackageComboboxSignal = 0
}) => {
    const { i18n } = useTranslation()

    const [shippingMethod, setShippingMethod] = useState(initialShippingMethod)
    const [initShippingPackages, setInitShippingPackages] = useState(false)
    const [shippingPackages, setShippingPackages] = useState([])
    const [isBusy, setIsBusy] = useState(false)
    const recalculatedShippingMethod = useRef(initialShippingMethod)

    const multiPackageShippingMethod = useMemo(() => {
        const multiPackageMethods = findMultiPackageShippingMethod(orderWarehouseId)

        if (!multiPackageMethods || multiPackageMethods?.length <= 0 || shippingPackages.length <= 1) {
            return null
        }

        return findShippingMethod(availableShippingMethods, multiPackageMethods?.[0], orderStoreId)
    }, [availableShippingMethods, orderWarehouseId])

    const handlePackageQuantityChange = (newPackageQuantity) => {
        const updatedShippingPackages = shippingPackages.map(shippingPackage => ({ ...shippingPackage }))

        while (updatedShippingPackages.length < newPackageQuantity) {
            updatedShippingPackages.push(null)
        }

        while (updatedShippingPackages.length > newPackageQuantity) {
            updatedShippingPackages.pop()
        }

        setShippingPackages(updatedShippingPackages)

        // Automatic multi package select is only enable if the shipping method is not editable and the initial method is not pickup
        if (!disableMultiPackageAutoSelect && !isShippingMethodEditable && initialShippingMethod.shipping_type !== SHIPPING_TYPES.PICKUP && multiPackageShippingMethod) {
            const newShippingMethod = newPackageQuantity > 1 ? multiPackageShippingMethod : recalculatedShippingMethod.current
            setShippingMethod(newShippingMethod)
            onShippingMethodChange(newShippingMethod, newPackageQuantity, updatedShippingPackages)
        } else {
            onPackageNumberChange(newPackageQuantity, updatedShippingPackages)
        }
    }

    const handleOnShippingPackageChange = (updatedShippingPackage, packageIndex) => {
        const shippingPackage = { ...updatedShippingPackage }

        if (updatedShippingPackage.package_type === PACKAGE_OPTIONS.PACKAGELESS) {
            shippingPackage.length_cm = null
            shippingPackage.width_cm = null
            shippingPackage.height_cm = null
            shippingPackage.weight_kg = null
            shippingPackage.volumetric_weight = null
        }

        const updatedShippingPackages = shippingPackages.map((currentShippingPackage, index) => (
            packageIndex === index ? shippingPackage : { ...currentShippingPackage }
        ))

        const selectedShippingPackage = updatedShippingPackages.filter(sp => sp.id != null)
        setShippingPackages(updatedShippingPackages)
        onShippingPackageChange(updatedShippingPackages)

        if (updatedShippingPackage.package_type != PACKAGE_OPTIONS.PACKAGELESS && selectedShippingPackage.length == updatedShippingPackages.length) { // && updatedShippingPackages.length == 1
            const packageWithBiggestVolumetric = updatedShippingPackages.reduce((prev, current) => (prev.volumetric_weight > current.volumetric_weight) ? prev : current)
            recalculateShippingMethod(packageWithBiggestVolumetric, updatedShippingPackages)
        }
    }

    const handleUpdatePackagelessPackage = (similarShippingPackage, packagelessPackageIndex) => {
        const packagelessPackage = { ...shippingPackages[packagelessPackageIndex] }

        packagelessPackage.length_cm = similarShippingPackage.length_cm
        packagelessPackage.width_cm = similarShippingPackage.width_cm
        packagelessPackage.height_cm = similarShippingPackage.height_cm
        packagelessPackage.weight_kg = similarShippingPackage.weight_kg
        packagelessPackage.volumetric_weight = similarShippingPackage.volumetric_weight

        const updatedShippingPackages = shippingPackages.map((currentShippingPackage, index) => (
            packagelessPackageIndex === index ? packagelessPackage : { ...currentShippingPackage }
        ))

        setShippingPackages(updatedShippingPackages)
        onShippingPackageChange(updatedShippingPackages)

        // const packageWithBiggestVolumetric = updatedShippingPackages.reduce((prev, current) => (prev.volumetric_weight > current.volumetric_weight) ? prev : current)
        recalculateShippingMethod(similarShippingPackage)
    }

    const setBusy = (newIsBusy) => {
        setIsBusy(newIsBusy)
        onBusyChange(newIsBusy)
    }

    const recalculateShippingMethod = async (shippingPackage, updatedShippingPackages) => {
        if (!orderId || !orderCanRecalculateShippingMethod) return
        if (shippingMethod.shipping_type === SHIPPING_TYPES.PICKUP) return
        setBusy(true)

        let shipping_package_ids = []
        if (updatedShippingPackages?.length > 1) {
            shipping_package_ids = updatedShippingPackages.map(sp => sp.id)
        } else {
            shipping_package_ids = [shippingPackage.id]
        }

        try {
            const newShippingMethod = await getBestShippingMethodForPackage(shipping_package_ids)

            if (!newShippingMethod) {
                // console.log("No shipping method returned by getBestShippingMethodForPackage")
                setBusy(false)
                return
            }
            if (newShippingMethod.id === shippingMethod.id) {
                // console.log("No need to update shipping method")
                setBusy(false)
                return
            }
            recalculatedShippingMethod.current = newShippingMethod
            setShippingMethod(newShippingMethod)
            onShippingMethodChange(newShippingMethod, updatedShippingPackages.length, updatedShippingPackages)
        } catch (error) {
            console.error("Error recalculateShippingMethod", error)
        }
        setBusy(false)
    }

    const getBestShippingMethodForPackage = async (shippingPackageIds) => {

        try {
            const response = await getShippingGroupCostsForPackage(orderId, shippingPackageIds)
            if (response.shipping_costs.length === 0) return null
            const bestShippingCost = response.shipping_costs[0]
            const bestShippingMethodId = bestShippingCost.shipping_method_id

            const newShippingMethod = availableShippingMethods.find(shippingMethod => shippingMethod.id === bestShippingMethodId)
            if (!newShippingMethod) {
                console.error(`Shipping method with id ${bestShippingMethodId} not found`)
                return null
            }

            return newShippingMethod

        } catch (error) {
            console.log("shipping costs error response", error.response)

            if (error.response?.status === 422) {
                let code = error.response.data.error_code
                if (code === "INVALID_PARAMETER") {
                    throw error
                }
                console.error("Error getting shipping costs", error)
            } else {
                throw error
            }
        }
    }

    useEffect(() => {
        if(!initShippingPackages){
            setInitShippingPackages(true)

            if(initialShippingPackages && initialShippingPackages.length > 0 && initialShippingPackages.length > initialPackageQuantity){
                throw new Error("Initial package quantity is greater than initial shipping packages quantity")
            }

            let newShippingPackages = initialShippingPackages ? [...initialShippingPackages] : new Array(initialPackageQuantity).fill(null)

            if(newShippingPackages.length < initialPackageQuantity){
                const missingQuantity = initialPackageQuantity - newShippingPackages.length
                newShippingPackages = newShippingPackages.concat(new Array(missingQuantity).fill(null))
            }

            setShippingPackages(newShippingPackages)
        }
    }, [shippingPackages, initialPackageQuantity, initialShippingPackages])

    const shouldAutofocusCombobox = STORE_IDS_FOR_COMBOBOX_AUTOFOCUS.includes(orderStoreId)

    return (
        <section className="bg-white rounded-lg">
            <div className="p-4">
                <h2 className="mb-0 text-2xl font-bold">{i18n.t('generate_shipment.shipping_package_data.title')}</h2>
                <p className="mb-0 text-lg">{i18n.t('generate_shipment.shipping_package_data.description')}</p>
            </div>

            <PackageQuantitySelect
                initialPackageQuantity={initialPackageQuantity}
                onUpdateQuantity={handlePackageQuantityChange}
                isEditable={isPackageQuantityEditable}
            />

            {shippingPackages.map((shippingPackage, index) => (
                <div key={`package-option-${index}`} className="border-t p-4">
                    <PackageSelect
                        initialShippingPackage={shippingPackage}
                        title={`${i18n.t('generate_shipment.shipping_package_data.package')} ${index + 1}`}
                        isPackagelessOrder={isPackagelessOrder}
                        hasPackagelessProduct={hasPackagelessProduct}
                        isFragileOrder={isFragileOrder}
                        storePackingOptions={storePackingOptions}
                        availableShippingPackages={availableShippingPackages}
                        onShippingPackageChange={(updatedShippingPackage) => handleOnShippingPackageChange(updatedShippingPackage, index)}
                        showVolumetricWeight={shippingPackage?.package_type !== PACKAGE_OPTIONS.PACKAGELESS}
                        orderStoreId={orderStoreId}
                        shouldAutofocus={shouldAutofocusCombobox && index === 0}
                        autofocusSignal={index === 0 ? focusPackageComboboxSignal : 0}
                    />

                    {shippingPackage?.package_type === PACKAGE_OPTIONS.PACKAGELESS && (
                        <div className="pt-4">
                            <div className="text-base font-medium">
                                {i18n.t('generate_shipment.shipping_package_data.select_similar_package')}
                            </div>
                            <PackageSelect
                                ignoreFragileType={true}
                                isFragileOrder={isFragileOrder}
                                storePackingOptions={storePackingOptions}
                                availableShippingPackages={availableShippingPackages}
                                onShippingPackageChange={(updatedShippingPackage) => handleUpdatePackagelessPackage(updatedShippingPackage, index)}
                                orderStoreId={orderStoreId}
                                shouldAutofocus={false}
                                autofocusSignal={0}
                            />
                        </div>
                    )}
                </div>
            ))}

            <ShippingMethodSelect
                initialShippingMethod={initialShippingMethod}
                shippingMethodOptions={shippingMethodOptions}
                isShippingMethodEditable={isShippingMethodEditable}
                onShippingMethodChange={
                    (sm) => {
                        setShippingMethod(sm)
                        onShippingMethodChange(sm)
                    }
                }
                value={shippingMethod}
                disabled={isBusy}
            />

        </section>
    )
}

export default ShippingPackageDataSection
